import React, { useMemo } from "react";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import MemoryIcon from "@mui/icons-material/Memory";
import SpeedIcon from "@mui/icons-material/Speed";
import TimelineIcon from "@mui/icons-material/Timeline";

const safeNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const formatValue = (value, suffix) => `${safeNumber(value).toFixed(2)} ${suffix}`;

const calculateSummary = (stats, key) => {
    if (!stats.length) return { current: 0, average: 0, max: 0 };

    const values = stats.map((item) => safeNumber(item[key]));
    const total = values.reduce((sum, value) => sum + value, 0);

    return {
        current: values[values.length - 1] || 0,
        average: total / values.length,
        max: Math.max(...values),
    };
};

function MetricSummaryCard({ title, icon, summary, suffix }) {
    return (
        <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
            <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {icon}
                        <Typography variant="subtitle2" color="text.secondary">
                            {title}
                        </Typography>
                    </Stack>
                    <Chip size="small" label={`Сейчас ${formatValue(summary.current, suffix)}`} />
                </Stack>

                <Stack direction="row" justifyContent="space-between" spacing={2} mt={2}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Среднее</Typography>
                        <Typography variant="h6">{formatValue(summary.average, suffix)}</Typography>
                    </Box>
                    <Box textAlign="right">
                        <Typography variant="caption" color="text.secondary">Пик</Typography>
                        <Typography variant="h6">{formatValue(summary.max, suffix)}</Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

function MetricChart({ data, dataKey, title, unit, domain }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 2 }, borderRadius: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {data.length} точек
                </Typography>
            </Stack>

            <Box sx={{ width: "100%", height: { xs: 230, sm: 280 } }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 8, right: isMobile ? 4 : 16, left: isMobile ? -20 : 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id={`gradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.42} />
                                <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.28} />
                        <XAxis
                            dataKey="time"
                            minTickGap={isMobile ? 40 : 24}
                            tick={{ fontSize: isMobile ? 10 : 12 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            domain={domain}
                            width={isMobile ? 42 : 55}
                            tick={{ fontSize: isMobile ? 10 : 12 }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(value) => `${value}${unit}`}
                        />
                        <Tooltip
                            content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const point = payload[0].payload;
                                return (
                                    <Paper elevation={6} sx={{ p: 1.25, borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {point.fullTime}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700}>
                                            {formatValue(point[dataKey], unit)}
                                        </Typography>
                                    </Paper>
                                );
                            }}
                        />
                        <Area
                            type="monotone"
                            dataKey={dataKey}
                            stroke={theme.palette.primary.main}
                            strokeWidth={2.5}
                            fill={`url(#gradient-${dataKey})`}
                            dot={false}
                            activeDot={{ r: 4 }}
                            isAnimationActive={false}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
        </Paper>
    );
}

export default function ServerStatsChart({ stats }) {
    const normalizedStats = Array.isArray(stats) ? stats : [];

    const cpuSummary = useMemo(() => calculateSummary(normalizedStats, "cpu"), [normalizedStats]);
    const memorySummary = useMemo(() => calculateSummary(normalizedStats, "memory"), [normalizedStats]);

    const memoryMax = useMemo(() => {
        const max = Math.max(0, ...normalizedStats.map((item) => safeNumber(item.memory)));
        if (max <= 8) return 8;
        if (max <= 16) return 16;
        if (max <= 32) return 32;
        if (max <= 64) return 64;
        return Math.ceil(max / 16) * 16;
    }, [normalizedStats]);

    if (!normalizedStats.length) {
        return (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
                <TimelineIcon color="disabled" sx={{ fontSize: 44, mb: 1 }} />
                <Typography variant="h6">Данных мониторинга пока нет</Typography>
                <Typography color="text.secondary">
                    Графики появятся после получения статистики от сервера.
                </Typography>
            </Paper>
        );
    }

    return (
        <Stack spacing={2.5}>
            <Box>
                <Typography variant="h5" fontWeight={800}>Мониторинг ресурсов</Typography>
                <Typography color="text.secondary">
                    Текущая нагрузка, средние значения и пики за выбранный период.
                </Typography>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <MetricSummaryCard
                        title="Процессор"
                        icon={<SpeedIcon color="primary" />}
                        summary={cpuSummary}
                        suffix="%"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <MetricSummaryCard
                        title="Оперативная память"
                        icon={<MemoryIcon color="primary" />}
                        summary={memorySummary}
                        suffix="ГБ"
                    />
                </Grid>
            </Grid>

            <MetricChart
                data={normalizedStats}
                dataKey="cpu"
                title="Загрузка CPU"
                unit="%"
                domain={[0, Math.max(100, Math.ceil(cpuSummary.max / 10) * 10)]}
            />

            <MetricChart
                data={normalizedStats}
                dataKey="memory"
                title="Использование памяти"
                unit="ГБ"
                domain={[0, memoryMax]}
            />
        </Stack>
    );
}
